import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, EmptyState, SearchSelect } from './ui';
import {
  IconCheck,
  IconChevronDown,
  IconDownload,
  IconSearch,
  IconSettings,
  IconX,
} from './icons';
import { formatDate, formatDateTime, formatRelative, SLA_LABELS, SLA_VARIANTS } from '../lib/format';

/**
 * Listenansicht der Aufgaben – die tabellarische Alternative zum Kanban-Board.
 *
 * Das Board zeigt den Fluss, die Liste den Bestand: Wer 60 offene Vorgänge
 * nach Objekt gruppiert und nach Frist sortiert sehen will, kommt mit Spalten
 * weiter als mit Karten. Sortierung, Gruppierung und Filter arbeiten
 * ausschließlich im Browser auf den bereits geladenen Daten – die Ansicht
 * reagiert damit ohne Serverabfrage und bleibt auch offline benutzbar.
 *
 * Die Einstellungen werden pro Gerät gespeichert; wer seine Ansicht einmal
 * eingerichtet hat, findet sie beim nächsten Aufruf wieder vor.
 */

/* ------------------------------------------------------------------ */
/* Spaltenmodell                                                       */
/* ------------------------------------------------------------------ */

type FilterType = 'text' | 'enum' | 'date' | 'none';

interface Column {
  key: string;
  label: string;
  filter: FilterType;
  /** Rohwert für Sortierung, Filterung und Gruppierung. */
  value: (task: any) => string | number | null;
  /** Anzeigewert in der Zelle. Fehlt er, wird `value` ausgegeben. */
  display?: (task: any) => ReactNode;
  /** Beschriftung in Gruppenköpfen und Filterlisten. */
  label_of?: (task: any) => string;
  width?: number;
  align?: 'right';
  /** Spalten ohne sinnvolle Gruppierung (Titel, Nummer) bleiben außen vor. */
  groupable?: boolean;
  numeric?: boolean;
}

const DUE_BUCKETS = ['Überfällig', 'Heute fällig', 'Diese Woche', 'Später', 'Ohne Frist'] as const;

/** Ordnet eine Frist einer Dringlichkeitsstufe zu – Grundlage der Gruppierung. */
function dueBucket(task: any): string {
  if (!task.due_at) return 'Ohne Frist';
  const due = new Date(task.due_at.includes('T') ? task.due_at : `${task.due_at.replace(' ', 'T')}Z`);
  const now = new Date();
  if (due.getTime() < now.getTime()) return 'Überfällig';
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);
  if (due <= endOfToday) return 'Heute fällig';
  const endOfWeek = new Date(endOfToday);
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  return due <= endOfWeek ? 'Diese Woche' : 'Später';
}

const COLUMNS: Column[] = [
  {
    key: 'task_no',
    label: 'Nummer',
    filter: 'text',
    width: 132,
    value: (t) => t.task_no ?? '',
    display: (t) => <span className="mono">{t.task_no}</span>,
  },
  {
    key: 'title',
    label: 'Titel',
    filter: 'text',
    width: 280,
    value: (t) => t.title ?? '',
    display: (t) => (
      <div>
        <div className="truncate" style={{ fontWeight: 600 }}>
          {t.title}
        </div>
        {t.location_detail && <div className="small subtle truncate">{t.location_detail}</div>}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    filter: 'enum',
    width: 168,
    groupable: true,
    value: (t) => t.statusLabel ?? t.status ?? '',
    label_of: (t) => t.statusLabel ?? t.status ?? '–',
    display: (t) => <Badge>{t.statusLabel ?? t.status}</Badge>,
  },
  {
    key: 'criticality',
    label: 'Kritikalität',
    filter: 'enum',
    width: 140,
    groupable: true,
    // Nach Stufe sortieren, nicht alphabetisch: Notfall gehört nach oben.
    value: (t) => t.criticality_level ?? 99,
    numeric: true,
    label_of: (t) => t.criticality_name ?? 'Ohne Stufe',
    display: (t) => <Badge color={t.criticality_color}>{t.criticality_name ?? 'Ohne Stufe'}</Badge>,
  },
  {
    key: 'sla',
    label: 'SLA',
    filter: 'enum',
    width: 130,
    groupable: true,
    value: (t) => SLA_LABELS[t.slaState] ?? t.slaState ?? '',
    label_of: (t) => SLA_LABELS[t.slaState] ?? t.slaState ?? '–',
    display: (t) => (
      <Badge variant={SLA_VARIANTS[t.slaState] ?? ''} dot>
        {SLA_LABELS[t.slaState] ?? t.slaState}
      </Badge>
    ),
  },
  {
    key: 'category',
    label: 'Gewerk',
    filter: 'enum',
    width: 150,
    groupable: true,
    value: (t) => t.category_name ?? '',
    label_of: (t) => t.category_name ?? 'Ohne Gewerk',
    display: (t) => (t.category_name ? <span className="chip">{t.category_name}</span> : <span className="subtle">–</span>),
  },
  {
    key: 'property',
    label: 'Objekt',
    filter: 'enum',
    width: 200,
    groupable: true,
    value: (t) => t.property_name ?? '',
    label_of: (t) => t.property_name ?? 'Ohne Objekt',
    display: (t) => <span className="truncate">{t.property_name ?? '–'}</span>,
  },
  {
    key: 'customer',
    label: 'Eigentümer/Verwalter',
    filter: 'enum',
    width: 180,
    groupable: true,
    value: (t) => t.customer_name ?? '',
    label_of: (t) => t.customer_name ?? 'Ohne Eigentümer/Verwalter',
    display: (t) => <span className="truncate small">{t.customer_name ?? '–'}</span>,
  },
  {
    key: 'assignee',
    label: 'Zuständig',
    filter: 'enum',
    width: 180,
    groupable: true,
    value: (t) => (t.assignee_first_name ? `${t.assignee_first_name} ${t.assignee_last_name}` : ''),
    label_of: (t) => (t.assignee_first_name ? `${t.assignee_first_name} ${t.assignee_last_name}` : 'Nicht zugewiesen'),
    display: (t) =>
      t.assignee_first_name ? (
        <div className="row" style={{ gap: 7, flexWrap: 'nowrap' }}>
          <Avatar firstName={t.assignee_first_name} lastName={t.assignee_last_name} size="sm" />
          <span className="truncate small">
            {t.assignee_first_name} {t.assignee_last_name}
          </span>
        </div>
      ) : (
        <Badge variant="warning">Offen</Badge>
      ),
  },
  {
    key: 'reporter',
    label: 'Melder',
    filter: 'text',
    width: 170,
    value: (t) =>
      t.reporter_user_first_name
        ? `${t.reporter_user_first_name} ${t.reporter_user_last_name}`
        : [t.reporter_first_name, t.reporter_name].filter(Boolean).join(' '),
    display: (t) => {
      const name = t.reporter_user_first_name
        ? `${t.reporter_user_first_name} ${t.reporter_user_last_name}`
        : [t.reporter_first_name, t.reporter_name].filter(Boolean).join(' ');
      return <span className="truncate small">{name || '–'}</span>;
    },
  },
  {
    key: 'channel',
    label: 'Kanal',
    filter: 'enum',
    width: 140,
    groupable: true,
    value: (t) => t.channel_name ?? '',
    label_of: (t) => t.channel_name ?? 'Unbekannt',
    display: (t) => <span className="small subtle">{t.channel_name ?? '–'}</span>,
  },
  {
    key: 'created_at',
    label: 'Gemeldet',
    filter: 'date',
    width: 150,
    groupable: true,
    value: (t) => (t.created_at ? Date.parse(t.created_at.replace(' ', 'T') + 'Z') : 0),
    numeric: true,
    label_of: (t) => formatDate(t.created_at),
    display: (t) => <span className="small">{formatDateTime(t.created_at)}</span>,
  },
  {
    key: 'due_at',
    label: 'Fällig',
    filter: 'date',
    width: 160,
    groupable: true,
    // Vorgänge ohne Frist ans Ende, nicht an den Anfang.
    value: (t) => (t.due_at ? Date.parse(t.due_at.replace(' ', 'T') + 'Z') : Number.MAX_SAFE_INTEGER),
    numeric: true,
    label_of: dueBucket,
    display: (t) =>
      t.due_at ? (
        <span className="small" style={{ color: t.slaState === 'overdue' ? 'var(--danger-text)' : undefined }}>
          {formatRelative(t.due_at)}
        </span>
      ) : (
        <span className="subtle small">–</span>
      ),
  },
  {
    key: 'scheduled_start',
    label: 'Geplant',
    filter: 'date',
    width: 150,
    groupable: true,
    value: (t) => (t.scheduled_start ? Date.parse(t.scheduled_start.replace(' ', 'T') + 'Z') : Number.MAX_SAFE_INTEGER),
    numeric: true,
    label_of: (t) => (t.scheduled_start ? formatDate(t.scheduled_start) : 'Nicht terminiert'),
    display: (t) => <span className="small">{t.scheduled_start ? formatDateTime(t.scheduled_start) : '–'}</span>,
  },
];

const DEFAULT_VISIBLE = [
  'task_no',
  'title',
  'status',
  'criticality',
  'sla',
  'property',
  'assignee',
  'due_at',
];

/* ------------------------------------------------------------------ */
/* Gespeicherte Ansicht                                                */
/* ------------------------------------------------------------------ */

interface ViewState {
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  groupBy: string;
  visible: string[];
  filters: Record<string, any>;
  search: string;
}

const EMPTY_VIEW: ViewState = {
  sort: { key: 'criticality', dir: 'asc' },
  groupBy: 'status',
  visible: DEFAULT_VISIBLE,
  filters: {},
  search: '',
};

function loadView(storageKey: string): ViewState {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return EMPTY_VIEW;
    // Unbekannte Spalten aus einer älteren Fassung stillschweigend verwerfen.
    const parsed = { ...EMPTY_VIEW, ...JSON.parse(raw) } as ViewState;
    parsed.visible = parsed.visible.filter((key) => COLUMNS.some((column) => column.key === key));
    if (parsed.visible.length === 0) parsed.visible = DEFAULT_VISIBLE;
    return parsed;
  } catch {
    return EMPTY_VIEW;
  }
}

/* ------------------------------------------------------------------ */
/* Komponente                                                          */
/* ------------------------------------------------------------------ */

export function TaskListView({
  tasks,
  users = [],
  onAssign,
  canAssign = false,
  storageKey = 'ff.taskList',
}: {
  tasks: any[];
  users?: any[];
  onAssign?: (taskId: number, assigneeId: number | null) => void;
  canAssign?: boolean;
  storageKey?: string;
}) {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewState>(() => loadView(storageKey));
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(view));
  }, [view, storageKey]);

  const patch = (change: Partial<ViewState>) => setView((current) => ({ ...current, ...change }));

  const visibleColumns = COLUMNS.filter((column) => view.visible.includes(column.key));

  /* ------------------------------ Filtern ------------------------------ */

  const filtered = useMemo(() => {
    const needle = view.search.trim().toLowerCase();

    return tasks.filter((task) => {
      // Freitextsuche über alle sichtbaren Spalten.
      if (needle) {
        const haystack = visibleColumns
          .map((column) => (column.label_of ? column.label_of(task) : String(column.value(task) ?? '')))
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }

      for (const column of COLUMNS) {
        const filter = view.filters[column.key];
        if (filter == null) continue;

        if (column.filter === 'text') {
          if (filter && !String(column.value(task) ?? '').toLowerCase().includes(String(filter).toLowerCase())) {
            return false;
          }
        } else if (column.filter === 'enum') {
          const selected: string[] = filter;
          if (selected.length > 0 && !selected.includes(labelOf(column, task))) return false;
        } else if (column.filter === 'date') {
          const raw = column.value(task);
          const time = typeof raw === 'number' && raw !== Number.MAX_SAFE_INTEGER ? raw : null;
          if (filter.from) {
            if (time == null || time < Date.parse(`${filter.from}T00:00:00`)) return false;
          }
          if (filter.to) {
            if (time == null || time > Date.parse(`${filter.to}T23:59:59`)) return false;
          }
        }
      }
      return true;
    });
  }, [tasks, view.filters, view.search, visibleColumns]);

  /* ----------------------------- Sortieren ----------------------------- */

  const sorted = useMemo(() => {
    if (!view.sort) return filtered;
    const column = COLUMNS.find((entry) => entry.key === view.sort!.key);
    if (!column) return filtered;

    const factor = view.sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const left = column.value(a);
      const right = column.value(b);
      if (column.numeric) return ((left as number) - (right as number)) * factor;
      return String(left).localeCompare(String(right), 'de', { numeric: true }) * factor;
    });
  }, [filtered, view.sort]);

  /* ---------------------------- Gruppieren ----------------------------- */

  const groups = useMemo(() => {
    if (!view.groupBy) return [{ key: '', label: '', tasks: sorted }];

    const column = COLUMNS.find((entry) => entry.key === view.groupBy);
    if (!column) return [{ key: '', label: '', tasks: sorted }];

    const map = new Map<string, any[]>();
    for (const task of sorted) {
      const label = labelOf(column, task);
      if (!map.has(label)) map.set(label, []);
      map.get(label)!.push(task);
    }

    const entries = [...map.entries()].map(([label, items]) => ({ key: label, label, tasks: items }));

    // Fristgruppen folgen der Dringlichkeit, alles andere der Sortierung
    // innerhalb der Gruppe – bei Kritikalität also der Stufe.
    if (column.key === 'due_at') {
      entries.sort((a, b) => DUE_BUCKETS.indexOf(a.label as any) - DUE_BUCKETS.indexOf(b.label as any));
    } else if (column.numeric) {
      entries.sort((a, b) => (column.value(a.tasks[0]) as number) - (column.value(b.tasks[0]) as number));
    } else {
      entries.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    }
    return entries;
  }, [sorted, view.groupBy]);

  const toggleSort = (key: string) => {
    setView((current) => {
      if (current.sort?.key !== key) return { ...current, sort: { key, dir: 'asc' } };
      if (current.sort.dir === 'asc') return { ...current, sort: { key, dir: 'desc' } };
      return { ...current, sort: null };
    });
  };

  const activeFilterCount = Object.entries(view.filters).filter(([, value]) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') return value.from || value.to;
    return Boolean(value);
  }).length;

  const exportCsv = () => {
    const header = visibleColumns.map((column) => column.label);
    const rows = sorted.map((task) =>
      visibleColumns.map((column) => (column.label_of ? column.label_of(task) : String(column.value(task) ?? ''))),
    );
    const escape = (value: string) => (/[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
    const csv = [header, ...rows].map((row) => row.map(escape).join(';')).join('\n');

    const link = document.createElement('a');
    // BOM voranstellen, sonst zeigt Excel Umlaute falsch an.
    link.href = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    link.download = `aufgaben-ansicht-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    // `fills`: Die Liste füllt die Resthöhe des Fensters, statt mitzuwachsen.
    // Nur so bleibt die waagerechte Scrollleiste am unteren Rand erreichbar –
    // sonst hängt sie unter dem Ende der Tabelle, bei zweihundert Zeilen also
    // weit außerhalb des Blickfelds.
    <div className="stack fills" style={{ gap: 12 }}>
      {/* ----------------------------- Werkzeugleiste ---------------------- */}
      <div className="list-toolbar">
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <input
            type="search"
            placeholder="In der Liste suchen …"
            value={view.search}
            onChange={(event) => patch({ search: event.target.value })}
            style={{ paddingLeft: 34 }}
          />
          <span style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-subtle)' }}>
            <IconSearch width={15} height={15} />
          </span>
        </div>

        <label className="toolbar-field">
          <span>Gruppieren</span>
          <select value={view.groupBy} onChange={(event) => patch({ groupBy: event.target.value })}>
            <option value="">Keine Gruppierung</option>
            {COLUMNS.filter((column) => column.groupable).map((column) => (
              <option key={column.key} value={column.key}>
                {column.label}
              </option>
            ))}
          </select>
        </label>

        <label className="toolbar-field">
          <span>Sortieren</span>
          <select
            value={view.sort ? `${view.sort.key}:${view.sort.dir}` : ''}
            onChange={(event) => {
              if (!event.target.value) return patch({ sort: null });
              const [key, dir] = event.target.value.split(':');
              patch({ sort: { key, dir: dir as 'asc' | 'desc' } });
            }}
          >
            <option value="">Ohne Sortierung</option>
            {COLUMNS.map((column) => (
              <optgroup key={column.key} label={column.label}>
                <option value={`${column.key}:asc`}>{column.label} ↑</option>
                <option value={`${column.key}:desc`}>{column.label} ↓</option>
              </optgroup>
            ))}
          </select>
        </label>

        <ColumnPicker
          visible={view.visible}
          onChange={(visible) => patch({ visible })}
        />

        {activeFilterCount > 0 && (
          <button className="btn sm" onClick={() => patch({ filters: {} })} title="Alle Spaltenfilter zurücksetzen">
            <IconX width={13} height={13} /> {activeFilterCount} Filter
          </button>
        )}

        <button className="btn sm" onClick={exportCsv} title="Angezeigte Zeilen als CSV speichern">
          <IconDownload width={13} height={13} /> Export
        </button>
      </div>

      <div className="row between small subtle" style={{ padding: '0 2px' }}>
        <span>
          {sorted.length === tasks.length
            ? `${tasks.length} Vorgänge`
            : `${sorted.length} von ${tasks.length} Vorgängen`}
          {view.groupBy && ` · ${groups.length} Gruppen`}
        </span>
        {view.groupBy && (
          <span className="row" style={{ gap: 8 }}>
            <button className="btn sm ghost" onClick={() => setCollapsed(new Set(groups.map((g) => g.key)))}>
              Alle zuklappen
            </button>
            <button className="btn sm ghost" onClick={() => setCollapsed(new Set())}>
              Alle aufklappen
            </button>
          </span>
        )}
      </div>

      {/* -------------------------------- Tabelle -------------------------- */}
      <div className="card table-card">
        <div className="table-wrap sticky-head">
          <table className="data task-list">
            <thead>
              <tr>
                {visibleColumns.map((column) => (
                  <th key={column.key} style={{ width: column.width, minWidth: column.width }}>
                    <div className="th-inner">
                      <button
                        className="th-sort"
                        onClick={() => toggleSort(column.key)}
                        title={`Nach ${column.label} sortieren`}
                      >
                        {column.label}
                        <span className={`sort-arrow ${view.sort?.key === column.key ? view.sort.dir : ''}`}>
                          {view.sort?.key === column.key ? (view.sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      </button>
                      {column.filter !== 'none' && (
                        <ColumnFilter
                          column={column}
                          tasks={tasks}
                          value={view.filters[column.key]}
                          onChange={(value) =>
                            patch({ filters: { ...view.filters, [column.key]: value } })
                          }
                          onClear={() => {
                            const next = { ...view.filters };
                            delete next[column.key];
                            patch({ filters: next });
                          }}
                        />
                      )}
                    </div>
                  </th>
                ))}
                {canAssign && <th style={{ width: 190 }}>Zuweisen</th>}
              </tr>
            </thead>

            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + (canAssign ? 1 : 0)}>
                    <EmptyState
                      title="Keine Treffer"
                      hint="Kein Vorgang entspricht der aktuellen Filterung."
                      action={
                        <button className="btn" onClick={() => patch({ filters: {}, search: '' })}>
                          Filter zurücksetzen
                        </button>
                      }
                    />
                  </td>
                </tr>
              )}

              {groups.map((group) => {
                const isCollapsed = collapsed.has(group.key);
                return (
                  <Fragment key={group.key || 'alle'}>
                    {view.groupBy && (
                      <tr className="group-row">
                        <td colSpan={visibleColumns.length + (canAssign ? 1 : 0)}>
                          <button
                            className="group-header"
                            onClick={() =>
                              setCollapsed((current) => {
                                const next = new Set(current);
                                next.has(group.key) ? next.delete(group.key) : next.add(group.key);
                                return next;
                              })
                            }
                            aria-expanded={!isCollapsed}
                          >
                            <IconChevronDown
                              width={14}
                              height={14}
                              style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 150ms' }}
                            />
                            <strong>{group.label}</strong>
                            <span className="count">{group.tasks.length}</span>
                            {group.tasks.some((task: any) => task.slaState === 'overdue') && (
                              <Badge variant="danger">
                                {group.tasks.filter((task: any) => task.slaState === 'overdue').length} überfällig
                              </Badge>
                            )}
                          </button>
                        </td>
                      </tr>
                    )}

                    {!isCollapsed &&
                      group.tasks.map((task: any) => (
                        <tr
                          key={task.id}
                          className={`clickable ${task.criticality_level === 1 ? 'emergency' : ''}`}
                          onClick={() => navigate(`/tasks/${task.id}`)}
                          style={{ borderLeft: `3px solid ${task.criticality_color ?? 'transparent'}` }}
                        >
                          {visibleColumns.map((column, columnIndex) => (
                            <td key={column.key} className={column.align === 'right' ? 'num' : undefined}>
                              {column.display ? column.display(task) : String(column.value(task) ?? '–')}
                            </td>
                          ))}
                          {canAssign && (
                            <td onClick={(event) => event.stopPropagation()}>
                              <SearchSelect
                                value={String(task.assignee_id ?? '')}
                                onChange={(wert) => onAssign?.(task.id, wert ? Number(wert) : null)}
                                options={(users ?? []).map((person: any) => ({
                                  value: String(person.id),
                                  label: `${person.first_name} ${person.last_name}`,
                                }))}
                                placeholder="Nicht zugewiesen"
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Beschriftung eines Wertes – für Gruppenköpfe und Filterlisten. */
function labelOf(column: Column, task: any): string {
  if (column.label_of) return column.label_of(task);
  const value = column.value(task);
  return value == null || value === '' ? '–' : String(value);
}

/* ------------------------------------------------------------------ */
/* Spaltenfilter                                                       */
/* ------------------------------------------------------------------ */

/**
 * Filter am Spaltenkopf. Je nach Spaltentyp Freitext, Mehrfachauswahl der
 * tatsächlich vorkommenden Werte oder ein Zeitraum.
 *
 * Bewusst nicht über die gemeinsame `Dropdown`-Komponente: Die schließt sich
 * bei jedem Klick im Menü – beim Ankreuzen mehrerer Werte wäre das hinderlich.
 */
function ColumnFilter({
  column,
  tasks,
  value,
  onChange,
  onClear,
}: {
  column: Column;
  tasks: any[];
  value: any;
  onChange: (value: any) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  /** Vorkommende Werte mit Häufigkeit – zeigt gleich, wie viel ein Filter trifft. */
  const options = useMemo(() => {
    if (column.filter !== 'enum') return [];
    const counts = new Map<string, number>();
    for (const task of tasks) {
      const label = labelOf(column, task);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0], 'de'));
  }, [column, tasks]);

  const active =
    column.filter === 'enum'
      ? Array.isArray(value) && value.length > 0
      : column.filter === 'date'
        ? Boolean(value?.from || value?.to)
        : Boolean(value);

  return (
    <div className="column-filter" ref={ref}>
      <button
        className={`filter-button ${active ? 'active' : ''}`}
        onClick={() => setOpen((current) => !current)}
        title={active ? 'Filter aktiv – zum Ändern klicken' : `Nach ${column.label} filtern`}
        aria-label={`Filter ${column.label}`}
      >
        <FilterGlyph />
      </button>

      {open && (
        <div className="filter-popover">
          {column.filter === 'text' && (
            <input
              autoFocus
              type="search"
              placeholder={`${column.label} enthält …`}
              value={value ?? ''}
              onChange={(event) => onChange(event.target.value)}
            />
          )}

          {column.filter === 'date' && (
            <div className="stack" style={{ gap: 8 }}>
              <label className="small subtle">Von</label>
              <input
                type="date"
                value={value?.from ?? ''}
                onChange={(event) => onChange({ ...(value ?? {}), from: event.target.value })}
              />
              <label className="small subtle">Bis</label>
              <input
                type="date"
                value={value?.to ?? ''}
                onChange={(event) => onChange({ ...(value ?? {}), to: event.target.value })}
              />
            </div>
          )}

          {column.filter === 'enum' && (
            <div className="filter-options">
              {options.map(([label, count]) => {
                const selected: string[] = Array.isArray(value) ? value : [];
                const checked = selected.includes(label);
                return (
                  <label key={label} className="filter-option">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        onChange(checked ? selected.filter((entry) => entry !== label) : [...selected, label])
                      }
                    />
                    <span className="truncate">{label}</span>
                    <span className="count">{count}</span>
                  </label>
                );
              })}
            </div>
          )}

          <div className="row between" style={{ marginTop: 10 }}>
            <button
              className="btn sm ghost"
              onClick={() => {
                onClear();
                setOpen(false);
              }}
            >
              Zurücksetzen
            </button>
            <button className="btn sm primary" onClick={() => setOpen(false)}>
              <IconCheck width={13} height={13} /> Fertig
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 5h18l-7 8v6l-4-2v-4z" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Spaltenauswahl                                                      */
/* ------------------------------------------------------------------ */

function ColumnPicker({ visible, onChange }: { visible: string[]; onChange: (visible: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  return (
    <div className="column-filter" ref={ref}>
      <button className="btn sm" onClick={() => setOpen((current) => !current)}>
        <IconSettings width={13} height={13} /> Spalten ({visible.length})
      </button>
      {open && (
        <div className="filter-popover" style={{ right: 0, left: 'auto' }}>
          <div className="filter-options">
            {COLUMNS.map((column) => {
              const checked = visible.includes(column.key);
              return (
                <label key={column.key} className="filter-option">
                  <input
                    type="checkbox"
                    checked={checked}
                    // Mindestens eine Spalte muss bleiben, sonst wäre die Tabelle leer.
                    disabled={checked && visible.length === 1}
                    onChange={() =>
                      onChange(
                        checked
                          ? visible.filter((key) => key !== column.key)
                          : COLUMNS.filter((entry) => entry.key === column.key || visible.includes(entry.key)).map(
                              (entry) => entry.key,
                            ),
                      )
                    }
                  />
                  <span>{column.label}</span>
                </label>
              );
            })}
          </div>
          <div className="row between" style={{ marginTop: 10 }}>
            <button className="btn sm ghost" onClick={() => onChange(DEFAULT_VISIBLE)}>
              Standard
            </button>
            <button className="btn sm primary" onClick={() => setOpen(false)}>
              Fertig
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
